import { Theme, makeStyles } from '@material-ui/core/styles';
import { sidebarConfig } from '@backstage/core-components';
import {
  MENU_ITEM_HEIGHT,
  TREE_LINE_OFFSET,
  SUBMENU_ICON_WIDTH,
  SELECTED_INDICATOR_WIDTH,
  SELECTED_INDICATOR_WIDTH_POPOUT,
  SELECTED_INDICATOR_VERTICAL_OFFSET,
  ACTIVE_BORDER_WIDTH,
  POPOUT_GAP,
  SIDEBAR_CHIP_MARGIN_TOP,
  SIDEBAR_CHIP_MARGIN_LEFT,
} from './constants';

// Shared styles used by both collapsed and expanded modes
export const useSharedStyles = makeStyles<Theme>(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    height: MENU_ITEM_HEIGHT,
    padding: 0,
    paddingRight: theme.spacing(3),
    color: theme.palette.navigation.color,
    '&:hover': {
      backgroundColor:
        theme.palette.navigation.navItem?.hoverBackground ?? '#404040',
    },
  },
  headerActive: {
    borderLeft: `solid ${ACTIVE_BORDER_WIDTH}px ${theme.palette.navigation.indicator}`,
    // Adjust icon margin to account for border width and change icon color
    '& $headerIcon': {
      marginLeft: -ACTIVE_BORDER_WIDTH,
      color: theme.palette.navigation.indicator,
    },
    // Change text color when active
    '& $headerText .MuiTypography-root': {
      color: theme.palette.navigation.indicator,
    },
  },
  headerIcon: {
    boxSizing: 'border-box',
    width: sidebarConfig.drawerWidthClosed, // 72px to match standard icon container
    minWidth: sidebarConfig.drawerWidthClosed,
    marginRight: -theme.spacing(2), // Match standard's negative margin
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.navigation.color,
  },
  headerText: {
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.navigation.color,
    '& .MuiTypography-root': {
      color: theme.palette.navigation.color,
      fontWeight: 'bold',
      fontSize: theme.typography.fontSize,
    },
  },
}));

// Collapsed-only styles
export const useCollapsedStyles = makeStyles<Theme>(theme => ({
  collapsedItem: {
    position: 'relative',
    // CSS-driven hover overlay using pseudo-element - appears instantly on hover
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: MENU_ITEM_HEIGHT,
      backgroundColor:
        theme.palette.navigation.navItem?.hoverBackground ?? '#404040',
      opacity: 0,
      pointerEvents: 'none',
      zIndex: 1,
    },
    '&:hover::before': {
      opacity: 1,
    },
  },
  // Force hover overlay visible when mouse is on main item (state-driven, immediate)
  collapsedItemHovered: {
    '&::before': {
      opacity: 1,
    },
  },
  // Subtle caret icon indicating submenu in collapsed mode
  collapsedCaret: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '1rem',
    color: theme.palette.navigation.color,
    zIndex: 2, // Above the hover overlay
    pointerEvents: 'none',
  },
  // Active state for collapsed caret (when parent has headerActive)
  collapsedCaretActive: {
    color: theme.palette.navigation.indicator,
  },
  popoutPaper: {
    backgroundColor: theme.palette.navigation.background,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[8],
    border: `1px solid ${theme.palette.navigation.navItem?.hoverBackground ?? '#404040'}`,
    minWidth: 180,
    marginLeft: POPOUT_GAP,
    overflow: 'hidden',
  },
  popoutHeader: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.navigation.navItem?.hoverBackground ?? '#404040'}`,
    color: theme.palette.navigation.color,
    fontWeight: 700,
    fontSize: '0.875rem',
    letterSpacing: '0.01em',
  },
  popoutList: {
    padding: theme.spacing(0.5, 0),
    '& a': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(2),
      position: 'relative',
      width: '100%', // Constrain width to popout container
      // Make submenu items smaller than main items
      '& .MuiTypography-root': {
        fontSize: '0.8rem',
      },
      // Focus style: use hover background instead of blue outline
      '&:focus, &:focus-visible': {
        outline: 'none',
        backgroundColor:
          theme.palette.navigation.navItem?.hoverBackground ?? '#404040',
      },
    },
    // Reduce icon container width in popout submenus
    '& a > div:first-child': {
      width: SUBMENU_ICON_WIDTH,
      minWidth: SUBMENU_ICON_WIDTH,
      marginRight: 0,
    },
    // Hide secondary action in popout submenus
    '& a > button, & a > [role="button"]': {
      display: 'none',
    },
    // Custom selected indicator for popout submenu items
    // Add padding to compensate for removed border so content doesn't shift
    '& a[aria-current="page"], & a.Mui-selected, & a[class*="selected"]': {
      borderLeft: 'none',
      paddingLeft: `${theme.spacing(1) + sidebarConfig.selectedIndicatorWidth}px`,
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: SELECTED_INDICATOR_VERTICAL_OFFSET,
        bottom: SELECTED_INDICATOR_VERTICAL_OFFSET,
        width: SELECTED_INDICATOR_WIDTH_POPOUT,
        backgroundColor: theme.palette.navigation.indicator,
        borderRadius: 0,
      },
    },
    // Sidebar items with a chip use SidebarItem's children (secondaryAction slot).
    // In the popout the label already has a different font size but the same
    // flex override applies via the shared .sidebar-item-with-chip wrapper.
    '& .sidebar-item-with-chip': {
      display: 'block',
      width: '100%',
      '& a': {
        '& .MuiTypography-root': {
          flex: '0 0 auto',
          width: 'auto',
        },
        '& > div:last-child': {
          width: 'auto',
          marginRight: 0,
          alignSelf: 'flex-start',
          marginTop: SIDEBAR_CHIP_MARGIN_TOP,
          marginLeft: SIDEBAR_CHIP_MARGIN_LEFT,
          '& span': { marginLeft: 0 },
        },
      },
    },
  },
  // Invisible bridge element for hover continuity between popout and main item
  hoverBridge: {
    position: 'absolute',
    top: 51, // Align with first submenu item (matches Popper offset of -51)
    right: '100%', // Extends leftward from the popout
    width: '100vw', // Covers to left edge of viewport
    height: 48, // Match main item icon height
    backgroundColor: 'transparent',
    pointerEvents: 'auto',
  },
  // Container for popout content with relative positioning for bridge
  popoutContainer: {
    position: 'relative',
  },
}));

// Expanded-only styles
export const useExpandedStyles = makeStyles<Theme>(theme => ({
  expandableRoot: {
    width: '100%',
  },
  expandIcon: {
    color: theme.palette.navigation.color,
    opacity: 0.7,
    transition: 'transform 200ms',
    flexShrink: 0,
  },
  expandIconExpanded: {
    transform: 'rotate(180deg)',
  },
  nestedList: {
    padding: 0,
    paddingRight: 8,
    position: 'relative',
    // Vertical tree line indicator
    '&::before': {
      content: '""',
      position: 'absolute',
      left: TREE_LINE_OFFSET, // Align with main menu items
      top: 0,
      bottom: 0,
      width: 1,
      backgroundColor:
        theme.palette.navigation.navItem?.hoverBackground ?? '#404040',
    },
    '& a': {
      marginLeft: TREE_LINE_OFFSET,
      paddingLeft: 4,
      borderRadius: 0,
      position: 'relative',
      // Make submenu items smaller than main items
      '& .MuiTypography-root': {
        fontSize: '0.8rem',
      },
      // Reduce icon container width in submenu items
      '& .MuiListItemIcon-root': {
        minWidth: 32,
      },
      // Hide secondary action in submenus - it takes up space and causes ellipsis
      '& button, & [role="button"]': {
        display: 'none',
      },
      // Reduce icon container width in submenus
      '& > div:first-child': {
        width: SUBMENU_ICON_WIDTH,
        minWidth: SUBMENU_ICON_WIDTH,
        marginRight: 0,
      },
      // Focus style: use hover background instead of blue outline
      '&:focus, &:focus-visible': {
        outline: 'none',
        backgroundColor:
          theme.palette.navigation.navItem?.hoverBackground ?? '#404040',
      },
    },
    // Custom selected indicator positioned on the tree line
    // Target selected submenu items - Backstage uses various selectors for active state
    '& a[aria-current="page"], & a.Mui-selected, & a[class*="selected"]': {
      // Remove any default border-left indicator with rounded corners
      // Add padding to compensate for removed border so content doesn't shift
      borderLeft: 'none',
      paddingLeft: `${4 + sidebarConfig.selectedIndicatorWidth}px`,
      '&::before': {
        content: '""',
        position: 'absolute',
        left: -2,
        top: SELECTED_INDICATOR_VERTICAL_OFFSET,
        bottom: SELECTED_INDICATOR_VERTICAL_OFFSET,
        width: SELECTED_INDICATOR_WIDTH,
        backgroundColor: theme.palette.navigation.indicator,
        borderRadius: 0,
      },
    },
  },
}));
